import * as React from "react";
import {
  Button,
  Checkbox,
  CheckboxProps,
  Form,
  Label,
  TextArea,
} from "semantic-ui-react";
import { isJsonParseException, JsonBinding } from "./adl-gen/runtime/json";
import { VEditor } from "./adl-veditor";

// A form for entry of an arbirary ADL value . It is provided with an initial value and its
// ADL type. The onApply() callback is made when the user presses the apply button on validated
// input.

export interface AdlEditorProps<T> {
  value: unknown;
  veditor: VEditor<T, unknown, unknown>;
  onCancel?(): void;
  onClose?(): void;
  onApply?(value: T): void;
  disabled?: boolean;
  allowRaw?: JsonBinding<T>;
  validate?(value: T): Promise<string | null>;
  /** Table name when editing a DB row; used to persist organisation config overrides across Raw/form toggles */
  tableName?: string;
  /** Raw config from API (dbrow.value.config). Used when parsed value omits allowedPublisherIdsForSearch. */
  organisationRawConfig?: unknown;
}

enum Mode {
  VE, // Edit with the generated ADL VEditor
  RAW, // Edit with a (validated) json multiline text field
}

function getOrganisationConfigOverrides(
  value: unknown
): OrganisationConfigOverrides | null {
  if (
    value == null ||
    typeof value !== "object" ||
    !("config" in value) ||
    value.config == null ||
    typeof value.config !== "object"
  ) {
    return null;
  }
  const config = value.config as { allowedPublisherIdsForSearch?: unknown };
  if (!Array.isArray(config.allowedPublisherIdsForSearch)) {
    return null;
  }
  const arr = config.allowedPublisherIdsForSearch as unknown[];
  if (!arr.every((x) => typeof x === "string")) {
    return null;
  }
  return { allowedPublisherIdsForSearch: arr as string[] };
}

/** Extract overrides from raw config (API response). Use when parsed value drops allowedPublisherIdsForSearch. */
function getOrganisationConfigOverridesFromRaw(
  raw: unknown
): OrganisationConfigOverrides | null {
  if (raw == null) {
    return null;
  }
  let config: unknown = raw;
  if (typeof raw === "string") {
    try {
      config = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof config !== "object" || config === null) {
    return null;
  }
  const c = config as { allowedPublisherIdsForSearch?: unknown };
  if (!Array.isArray(c.allowedPublisherIdsForSearch)) {
    return null;
  }
  const arr = c.allowedPublisherIdsForSearch as unknown[];
  if (!arr.every((x) => typeof x === "string")) {
    return null;
  }
  return { allowedPublisherIdsForSearch: arr as string[] };
}

function mergeOrganisationOverridesIntoJson(
  row: Record<string, unknown>,
  overrides: OrganisationConfigOverrides
): void {
  let config = row.config;
  if (config == null || typeof config !== "object") {
    config = {};
    row.config = config;
  }
  (config as Record<string, unknown>).allowedPublisherIdsForSearch =
    overrides.allowedPublisherIdsForSearch;
}

interface OrganisationConfigOverrides {
  allowedPublisherIdsForSearch: string[];
}

interface AwaitingValidation {
  type: "awaiting";
  validationSeq: number;
}

interface FormError {
  type: "error";
  error: string;
  validationSeq: number;
}

interface FormValidated {
  type: "ok";
  validationSeq: number;
}

type FormValidation = AwaitingValidation | FormError | FormValidated;

interface OrganisationConfigOverrides {
  allowedPublisherIdsForSearch: string[];
}

interface AdlEditorState {
  value0: unknown;
  adlState: unknown;
  rawState: string;
  mode: Mode;
  pristine: boolean;
  formValidation: FormValidation;
  /** Persisted across Raw/form toggles when tableName === "organisation" (form has no widget for it) */
  organisationConfigOverrides: OrganisationConfigOverrides | null;
}

export class AdlEditor extends React.Component<
  AdlEditorProps<unknown>,
  AdlEditorState
> {
  // Internal state, which is re-initialized every time props change
  // @ts-ignore
  state: AdlEditorState;

  constructor(props: AdlEditorProps<unknown>) {
    super(props);
    this.initState(props);
  }

  componentDidUpdate(prevProps: AdlEditorProps<unknown>): void {
    if (
      prevProps.value !== this.props.value ||
      prevProps.organisationRawConfig !== this.props.organisationRawConfig
    ) {
      this.initState(this.props);
    }
  }

  initState(props: AdlEditorProps<unknown>) {
    const isOrg = props.tableName === "organisation";
    const fromValue =
      isOrg && props.value != null
        ? getOrganisationConfigOverrides(props.value)
        : null;
    const fromRaw =
      isOrg && props.organisationRawConfig != null
        ? getOrganisationConfigOverridesFromRaw(props.organisationRawConfig)
        : null;
    const organisationConfigOverrides = fromValue ?? fromRaw ?? null;

    this.state = {
      value0: props.value,
      adlState: this.mkState(props.value),
      pristine: true,
      rawState: this.mkRawState(props.value, organisationConfigOverrides),
      mode: Mode.VE,
      formValidation: {
        type: "ok",
        validationSeq: 0,
      },
      organisationConfigOverrides,
    };
  }

  mkState(value: unknown | null): unknown {
    return value === null
      ? this.props.veditor.initialState
      : this.props.veditor.stateFromValue(value);
  }

  mkRawState(
    value: unknown,
    overrides?: OrganisationConfigOverrides | null
  ): string {
    if (value === null || !this.props.allowRaw) {
      return "";
    }
    const row = this.props.allowRaw.toJson(value) as Record<string, unknown>;
    if (this.props.tableName === "organisation") {
      const toMerge =
        getOrganisationConfigOverrides(value) ?? overrides ?? null;
      if (toMerge != null) {
        mergeOrganisationOverridesIntoJson(row, toMerge);
      }
    }
    return JSON.stringify(row, null, 2);
  }

  render() {
    let errors: string[] = [];
    let renderedEditor: JSX.Element | null = null;

    switch (this.state?.mode) {
      case Mode.VE:
        errors = this.props.veditor.validate(this.state.adlState);
        renderedEditor = this.props.veditor.render(
          this.state.adlState,
          !this.props.disabled,
          this.onUpdate.bind(this)
        );
        break;
      case Mode.RAW:
        const result = this.parseRawText(this.state.rawState);
        const error =
          result.kind === "error" ? (
            <Label basic color="red">
              {result.error}
            </Label>
          ) : null;
        renderedEditor = (
          <div>
            <Form>
              <TextArea
                rows={20}
                style={{ fontFamily: "monospace" }}
                disabled={this.props.disabled}
                value={this.state.rawState}
                onChange={this.onRawChange}
              />
            </Form>
            {error}
          </div>
        );
        if (result.kind === "error") {
          errors = [result.error];
        }
        break;
      default:
    }

    const buttons: JSX.Element[] = [];
    if (this.props.onApply) {
      buttons.push(
        <Button
          primary
          loading={this.state?.formValidation.type === "awaiting"}
          disabled={errors.length > 0}
          key="Apply"
          onClick={this.onApply.bind(this)}
        >
          Apply
        </Button>
      );
    }
    if (this.props.onClose) {
      buttons.push(
        <Button key="Close" onClick={this.props.onClose}>
          Close
        </Button>
      );
    }
    if (this.props.onCancel) {
      buttons.push(
        <Button key="Cancel" onClick={this.props.onCancel}>
          Cancel
        </Button>
      );
    }

    let rawToggle: JSX.Element | null = null;
    if (this.props.allowRaw) {
      rawToggle = (
        <Checkbox
          toggle
          label="Edit raw"
          style={{ float: "right", margin: "8px" }}
          onClick={this.onToggleMode}
          checked={this.state?.mode === Mode.RAW}
        />
      );
    }

    let formError: JSX.Element | null = null;
    if (this.state?.formValidation.type === "error") {
      formError = (
        <div style={{ margin: "20px" }}>
          <Label basic color="red">
            {this.state.formValidation.error}
          </Label>
        </div>
      );
    }

    return (
      <div>
        <div>
          <div style={{ margin: "20px" }}>{renderedEditor}</div>
          {formError}
          <div style={{ margin: "20px" }}>
            {buttons}
            {rawToggle}
          </div>
        </div>
      </div>
    );
  }

  onToggleMode = (
    _event: React.SyntheticEvent<HTMLInputElement, MouseEvent>,
    _data: CheckboxProps
  ) => {
    switch (this.state?.mode) {
      case Mode.VE: {
        // Update the rawstate if we can, before switching to raw mode.
        // Prefer value from Config list; merge overrides when serialization drops allowedPublisherIdsForSearch.
        const errors = this.props.veditor.validate(this.state.adlState);
        if (errors.length === 0 && this.props.allowRaw) {
          const value = this.props.veditor.valueFromState(this.state.adlState);
          const rawState = this.mkRawState(
            value,
            this.state.organisationConfigOverrides
          );
          this.setState({ mode: Mode.RAW, rawState });
        } else {
          this.setState({ mode: Mode.RAW });
        }
        break;
      }

      case Mode.RAW: {
        // Update the veditor if we can, before switching to VE mode.
        // Persist organisation config overrides from raw so they survive form↔raw toggles.
        const result = this.parseRawText(this.state.rawState);
        let nextOverrides: OrganisationConfigOverrides | null = null;
        if (this.props.tableName === "organisation") {
          if (result.kind === "value") {
            nextOverrides =
              getOrganisationConfigOverrides(result.value) ??
              this.state.organisationConfigOverrides ??
              (() => {
                try {
                  const raw = JSON.parse(this.state.rawState) as Record<
                    string,
                    unknown
                  >;
                  return getOrganisationConfigOverridesFromRaw(raw?.config);
                } catch {
                  return null;
                }
              })();
          } else {
            nextOverrides = this.state.organisationConfigOverrides;
          }
        }
        if (result.kind === "value") {
          const adlState = this.props.veditor.stateFromValue(result.value);
          this.setState({
            mode: Mode.VE,
            adlState,
            organisationConfigOverrides: nextOverrides,
          });
        } else {
          this.setState({
            mode: Mode.VE,
            organisationConfigOverrides: nextOverrides,
          });
        }
        break;
      }
      default:
    }
  };

  // tslint:disable-next-line: no-any
  onRawChange = (_event: any, data: any) => {
    this.setState({ rawState: data.value });
  };

  parseRawText = (
    text: string
  ): { kind: "error"; error: string } | { kind: "value"; value: unknown } => {
    try {
      const jv = JSON.parse(text);
      if (this.props.allowRaw) {
        const value = this.props.allowRaw.fromJson(jv);
        return { kind: "value", value };
      }
      return { kind: "error", error: "BUG: no allowRaw property" };
    } catch (e) {
      if (e instanceof SyntaxError) {
        return { kind: "error", error: "Json is not well formed" };
      } else if (isJsonParseException(e)) {
        return { kind: "error", error: e.getMessage() };
      } else {
        return { kind: "error", error: "Unknown error" };
      }
    }
  };

  // tslint:disable-next-line: no-any
  onUpdate(event: any) {
    const newAdlState = this.props.veditor.update(this.state?.adlState, event);
    this.setState({
      adlState: newAdlState,
      pristine: false,
    });
    void this.validateForm(newAdlState);
  }

  // tslint:disable-next-line: no-any
  async validateForm(adlState: any) {
    if (this.props.validate && this.state?.mode === Mode.VE) {
      const value = this.props.veditor.valueFromState(adlState);
      const validationSeq = this.state.formValidation.validationSeq + 1;
      this.setState({
        formValidation: {
          type: "awaiting",
          validationSeq,
        },
      });
      const error: string | null = await this.props.validate(value);
      if (validationSeq === this.state.formValidation.validationSeq) {
        if (error && error.length > 0) {
          this.setState({
            formValidation: {
              type: "error",
              error,
              validationSeq,
            },
          });
        } else {
          this.setState({
            formValidation: {
              type: "ok",
              validationSeq,
            },
          });
        }
      }
    }
  }

  onRevert() {
    this.setState({
      adlState: this.mkState(this.state?.value0),
      pristine: true,
    });
  }

  onApply() {
    let value: unknown = null;
    switch (this.state?.mode) {
      case Mode.VE: {
        value = this.props.veditor.valueFromState(this.state.adlState);
        // Config list (Vector editor) is source of truth; value already has
        // allowedPublisherIdsForSearch. No overwrite with overrides.
        break;
      }
      case Mode.RAW: {
        if (this.props.allowRaw) {
          value = this.props.allowRaw.fromJson(JSON.parse(this.state.rawState));
          // Raw parse drops allowedPublisherIdsForSearch; extract from raw JSON and merge so we don't overwrite with [].
          if (
            this.props.tableName === "organisation" &&
            value != null &&
            typeof value === "object"
          ) {
            let overrides: OrganisationConfigOverrides | null = null;
            try {
              const raw = JSON.parse(this.state.rawState) as Record<
                string,
                unknown
              >;
              overrides = getOrganisationConfigOverridesFromRaw(raw?.config);
            } catch {
              overrides = this.state.organisationConfigOverrides;
            }
            overrides = overrides ?? this.state.organisationConfigOverrides;
            if (overrides != null) {
              const v = value as Record<string, unknown>;
              let config = v.config;
              if (config == null || typeof config !== "object") {
                config = {};
                v.config = config;
              }
              (config as Record<string, unknown>).allowedPublisherIdsForSearch =
                overrides.allowedPublisherIdsForSearch;
            }
          }
        }
        break;
      }
      default:
    }
    if (this.props.onApply) {
      this.props.onApply(value);
      this.setState({
        value0: value,
        pristine: true,
      });
    }
  }
}
